import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, CheckCircle, XCircle, MessageSquare, Clock, Bell, X } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow, format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import type { BuyerNotification } from '@/hooks/useBuyerNotifications';

interface NotificationDetailDialogProps {
  notification: BuyerNotification | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NotificationIcon = ({ type, size = 'md' }: { type: BuyerNotification['notification_type']; size?: 'sm' | 'md' }) => {
  const sizeClass = size === 'md' ? 'h-6 w-6' : 'h-4 w-4';
  
  switch (type) {
    case 'viewed':
      return <Eye className={cn(sizeClass, 'text-blue-500')} />;
    case 'approved':
      return <CheckCircle className={cn(sizeClass, 'text-green-500')} />;
    case 'denied':
      return <XCircle className={cn(sizeClass, 'text-destructive')} />;
    case 'counter':
      return <MessageSquare className={cn(sizeClass, 'text-amber-500')} />;
    case 'expired':
      return <Clock className={cn(sizeClass, 'text-muted-foreground')} />;
    default:
      return <Bell className={cn(sizeClass, 'text-foreground')} />;
  }
};

function getNotificationTypeLabel(type: BuyerNotification['notification_type']): string {
  switch (type) {
    case 'viewed':
      return 'Deal Viewed';
    case 'approved':
      return 'Deal Approved';
    case 'denied':
      return 'Deal Declined';
    case 'counter':
      return 'Counter Offer';
    case 'expired':
      return 'Deal Expired';
    default:
      return 'Notification';
  }
}

function getNotificationBadgeVariant(type: BuyerNotification['notification_type']): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (type) {
    case 'approved':
      return 'default';
    case 'denied':
    case 'expired':
      return 'destructive';
    case 'counter':
    case 'viewed':
      return 'secondary';
    default:
      return 'outline';
  }
}

export function NotificationDetailDialog({ notification, open, onOpenChange }: NotificationDetailDialogProps) {
  if (!notification) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] flex flex-col">
        <DialogHeader className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-muted">
              <NotificationIcon type={notification.notification_type} size="md" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-lg">{notification.title}</DialogTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={getNotificationBadgeVariant(notification.notification_type)}>
                  {getNotificationTypeLabel(notification.notification_type)}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                </span>
              </div>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 mt-4 max-h-[300px]">
          <DialogDescription asChild>
            <div className="space-y-4">
              <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                {notification.message}
              </p>
              
              {notification.dealer_name && (
                <div className="pt-2 border-t">
                  <span className="text-xs text-muted-foreground">From: </span>
                  <span className="text-sm font-medium">{notification.dealer_name}</span>
                </div>
              )}
              
              <div className="text-xs text-muted-foreground pt-2">
                Received: {format(new Date(notification.created_at), 'PPpp')}
              </div>
            </div>
          </DialogDescription>
        </ScrollArea>

        <div className="flex justify-between items-center gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {notification.deal_id && (
            <Button asChild>
              <Link to="/account" onClick={() => onOpenChange(false)}>
                View My Deals
              </Link>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
